import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const useAgentChatStore = create(
  persist(
    (set, get) => ({
  // Panel state
  isOpen: false,
  isStreaming: false,
  activeTab: 'ai', // 'ai' | 'people'

  // Messages: { id, role: 'user'|'assistant', content, toolResults?, timestamp }
  messages: [],
  messageIdCounter: 0,

  // Conversations unread count (set by AgentChatButton, read by AgentChat tab badge)
  conversationUnreadCount: 0,

  // Error state
  error: null,

  // Actions
  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  setConversationUnreadCount: (count) => set({ conversationUnreadCount: count }),

  setStreaming: (isStreaming) => set({ isStreaming }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),

  addUserMessage: (content, attachments = null) => {
    const { messageIdCounter } = get();
    const id = messageIdCounter + 1;
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id,
          role: 'user',
          content,
          ...(attachments?.length ? { attachments } : {}),
          timestamp: Date.now(),
        },
      ],
      messageIdCounter: id,
      error: null,
    }));
    return id;
  },

  addAssistantMessage: () => {
    const { messageIdCounter } = get();
    const id = messageIdCounter + 1;
    set((state) => ({
      messages: [
        ...state.messages,
        { id, role: 'assistant', content: '', toolCalls: [], toolResults: [], timestamp: Date.now() },
      ],
      messageIdCounter: id,
    }));
    return id;
  },

  appendToAssistant: (text) => {
    set((state) => {
      const msgs = [...state.messages];
      const lastAssistant = msgs.findLast((m) => m.role === 'assistant');
      if (lastAssistant) {
        lastAssistant.content += text;
      }
      return { messages: msgs };
    });
  },

  addToolCall: (toolName, input) => {
    set((state) => {
      const msgs = [...state.messages];
      const lastAssistant = msgs.findLast((m) => m.role === 'assistant');
      if (lastAssistant) {
        lastAssistant.toolCalls = [
          ...(lastAssistant.toolCalls || []),
          { toolName, input },
        ];
      }
      return { messages: msgs };
    });
  },

  addToolResult: (toolName, result) => {
    set((state) => {
      const msgs = [...state.messages];
      const lastAssistant = msgs.findLast((m) => m.role === 'assistant');
      if (lastAssistant) {
        lastAssistant.toolResults = [
          ...(lastAssistant.toolResults || []),
          { toolName, result },
        ];
      }
      return { messages: msgs };
    });
  },

  setResolvedStudents: (students) => {
    set((state) => {
      const msgs = [...state.messages];
      const lastAssistant = msgs.findLast((m) => m.role === 'assistant');
      if (lastAssistant) {
        lastAssistant.resolvedStudents = students;
      }
      return { messages: msgs };
    });
  },

  setDraftBatchId: (batchId) => {
    set((state) => {
      const msgs = [...state.messages];
      const lastAssistant = msgs.findLast((m) => m.role === 'assistant');
      if (lastAssistant) {
        lastAssistant.draftBatchId = batchId;
      }
      return { messages: msgs };
    });
  },

  // Get messages formatted for the API (role + content, tool history, attachments on user msgs, last 20)
  getApiMessages: () => {
    const { messages } = get();
    return messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({
        role: m.role,
        content: m.content,
        ...(m.attachments?.length ? { attachments: m.attachments } : {}),
        ...(m.toolCalls?.length ? { toolCalls: m.toolCalls } : {}),
        ...(m.toolResults?.length ? { toolResults: m.toolResults } : {}),
      }))
      .slice(-20);
  },

  clearMessages: () => set({ messages: [], messageIdCounter: 0, error: null }),
    }),
    {
      name: 'swan-agent-chat',
      partialize: (state) => ({
        messages: state.messages,
        messageIdCounter: state.messageIdCounter,
        activeTab: state.activeTab,
      }),
    }
  )
);

export default useAgentChatStore;
